import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router';
import { BsFillPersonLinesFill, CgDetailsMore, BsFillInfoCircleFill } from 'react-icons/all';
import * as _ from 'lodash';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Promise from 'bluebird';
import BasicInformationField from '../components/basic/BasicInformationField';
import getTheme from '../theme/theme';
import CollectionItemHeader from '../components/layout/CollectionItemHeader';
import BasicField from '../components/basic/BasicField';
import AppButtonMinimal from '../components/basic/AppButtonMinimal';
import AppLoader from '../components/basic/AppLoader';
import { useUploadData } from '../hooks/uploads-hooks';
import NoItemsSelectedImg from '../assets/basic-profile-image.jpg';
import { useUpdateUser } from '../hooks/user-hooks';
import { useAuth } from '../AuthCtx';
import { fileToDataURL } from '../hooks/utils';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      minWidth: 275,
    },
    paper: {
      minWidth: 500,
      maxWidth: 800,
      width: '100%',
      padding: theme.spacing(2, 1),
    },
  };
});

function AccountPage() {
  const [ userEdit, setUserEdit ] = useState({});
  const classes = useStyles();

  const history = useHistory();

  const { user, userIsAuthenticating } = useAuth();
  const { updateUser, userIsUpdating } = useUpdateUser();
  const { uploadData, uploadIsLoading } = useUploadData();

  const [ dataUri, setDataUri ] = useState('');
  const formRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!user.avatar?.url) {
      setDataUri('');
      return;
    }
    setDataUri(`${ process.env.REACT_APP_API_URL }${ user.avatar.url }`);
  }, [ user, setDataUri ]);

  const onFileSelected = (file) => {
    if (file) {
      onUserPropertyChange('avatar', 'sss');
      fileToDataURL(file)
        .then((tempUri) => {
          setDataUri(tempUri);
        });
    }
  };

  useEffect(() => {
    setUserEdit((prevUserEdit) => {
      if (_.isEqual(user, prevUserEdit)) {
        return prevUserEdit;
      }

      return user;
    });
  }, [ user, setUserEdit ]);

  const onUserPropertyChange = (key, val) => {
    setUserEdit((prevUserEdit) => {
      return {
        ...prevUserEdit,
        [key]: val,
      };
    });
  };

  const onSave = (newUser) => {
    const uploadPromise = () => {
      const emptyPromise = Promise.try(() => {
        return userEdit.avatar;
      });
      if (!formRef.current) {
        return emptyPromise;
      }
      const bodyFormData = new FormData(formRef.current);

      const file = bodyFormData.get('files');
      if (!file || !file.name || !file.size) {
        return emptyPromise;
      }

      bodyFormData.append('refId', `${ user.id }`);
      bodyFormData.append('ref', 'admin-tool-user');
      bodyFormData.append('field', 'avatar');

      return Promise.try(() => {
        return uploadData(bodyFormData);
      });
    };
    return uploadPromise().then((newAvatar) => {
      return updateUser({ ...newUser, avatar: newAvatar });
    }).then(() => {
      formRef.current.reset();
    });
  };

  const deleteAvatarFunction = () => {
    formRef.current.reset();
    setDataUri('');
    onUserPropertyChange('avatar', null);
  };

  if (userIsAuthenticating) {
    return <AppLoader />;
  }

  const userEditableBasicDetails = [
    {
      field: 'name',
      label: 'Name',
      disabled: false,
    },
    {
      field: 'family_name',
      label: 'Surname',
      disabled: false,
    },
    {
      field: 'phone_number',
      label: 'Phone Number',
      disabled: false,
    },
  ];

  const userLeftNonEditableBasicDetails = [
    {
      field: 'username',
      label: 'Username',
      disabled: true,
    },
    {
      field: 'email',
      label: 'Email',
      disabled: true,
    },
    {
      field: 'role',
      label: 'Role',
      disabled: true,
    },
  ];

  const userRightNonEditableBasicDetails = [
    {
      field: 'provider',
      label: 'Provider',
      disabled: true,
    },
    {
      field: 'sub',
      label: 'Cognito ID',
      disabled: true,
    },
    {
      field: 'confirmed',
      label: 'Confirmed',
      disabled: true,
    },
  ];

  const userAccountDetails = [
    {
      field: 'updated_at',
      label: 'UPDATED',
      disabled: true,
    },
    {
      field: 'published_at',
      label: 'PUBLISHED',
      disabled: true,
    },
    {
      field: 'created_at',
      label: 'CREATED',
      disabled: true,
    },
  ];

  return (
    <div className={ classes.root }>
      <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: getTheme().spacing(2) } }>
        <BsFillPersonLinesFill
          style={
            {
              color: getTheme().palette.secondary.dark,
              marginRight: getTheme().spacing(2),
            }
          }
          size={ 36 }
        />
        <CollectionItemHeader
          title='My Account'
          subtitle={ `ID: ${ user.id }` }
          actions={ (
            <>
              <div />
              <AppButtonMinimal
                color={ getTheme().palette.warning.main }
                text='Logout'
                onClick={ () => {
                  return history.push('/logout');
                } }
              />
            </>
          ) }
        />
      </div>
      <Grid container spacing={ 3 }>
        <Grid item xs={ 12 } md={ 8 }>
          <Grid container spacing={ 3 } direction='column'>
            <Grid item>
              <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: getTheme().spacing(2) } }>
                <div style={ { display: 'flex', alignItems: 'center', flexDirection: 'row' } }>
                  <CgDetailsMore style={ { marginRight: getTheme().spacing(2) } } size={ 24 } />
                  <Typography variant='h6'>
                    Account Settings
                  </Typography>
                </div>
                <div style={ { display: 'flex', alignItems: 'center', flexDirection: 'row' } }>
                  <AppButtonMinimal
                    text='Reset'
                    color={ getTheme().palette.warning.main }
                    disabled={ _.isEqual(user, userEdit) }
                    onClick={ () => {
                      formRef.current.reset();
                      if (user?.avatar?.url) {
                        setDataUri(`${ process.env.REACT_APP_API_URL }${ user.avatar.url }`);
                      } else {
                        setDataUri('');
                      }
                      return setUserEdit(user);
                    } }
                  />
                  <div style={ { width: getTheme().spacing(1) } } />
                  <AppButtonMinimal
                    text={ userIsUpdating || uploadIsLoading ? 'Saving ...' : 'Save' }
                    disabled={ _.isEqual(user, userEdit) || userIsUpdating || uploadIsLoading }
                    onClick={ () => {
                      return onSave(userEdit);
                    } }
                  />
                </div>
              </div>
              <Card style={ { paddingBottom: getTheme().spacing(2) } }>
                <Grid container spacing={ 3 }>
                  <Grid item xs={ 6 }>
                    <CardContent>
                      {
                        userEditableBasicDetails.map(({ field, label, disabled }) => {
                          return (
                            <BasicField
                              key={ field }
                              onChange={ (v) => {
                                return onUserPropertyChange(field, v);
                              } }
                              disabled={ disabled }
                              title={ label }
                              field={ userEdit[field] }
                            />
                          );
                        })
                      }
                    </CardContent>
                  </Grid>
                  <Grid item xs={ 6 }>
                    <Grid container spacing={ 12 }>
                      <Grid item xs={ 12 }>
                        <CardContent>
                          <Typography spacing={ 3 }>AVATAR</Typography>
                          <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: getTheme().spacing(2) } }>
                            <img
                              width='auto'
                              height='200'
                              src={ '' === dataUri ? NoItemsSelectedImg : dataUri }
                              alt='avatar'
                            />
                          </div>
                          <form ref={ formRef }>
                            <input
                              ref={ inputRef }
                              type='file'
                              onChange={ (event) => { return onFileSelected(event.target.files[0] || null); } }
                              name='files'
                            />
                          </form>
                        </CardContent>
                        <Grid item xs={ 12 }>
                          <AppButtonMinimal
                            text='DELETE AVATAR'
                            color={ getTheme().palette.warning.main }
                            onClick={ deleteAvatarFunction }
                            disabled={ '' === dataUri }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item>
              <Card>
                <Grid container spacing={ 3 }>
                  <Grid item xs={ 6 }>
                    <CardContent>
                      {
                        userLeftNonEditableBasicDetails.map(({ field, label, disabled }) => {
                          return (
                            <BasicField
                              key={ field }
                              onChange={ (v) => {
                                return onUserPropertyChange(field, v);
                              } }
                              disabled={ disabled }
                              title={ label }
                              field={ 'role' === field ? userEdit[field]?.name : userEdit[field] }
                            />
                          );
                        })
                      }
                    </CardContent>
                  </Grid>
                  <Grid item xs={ 6 }>
                    <CardContent>
                      {
                        userRightNonEditableBasicDetails.map(({ field, label, disabled }) => {
                          return (
                            <BasicField
                              key={ field }
                              onChange={ (v) => {
                                return onUserPropertyChange(field, v);
                              } }
                              disabled={ disabled }
                              title={ label }
                              field={ userEdit[field] }
                            />
                          );
                        })
                      }
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={ 12 } md={ 4 }>
          <Grid container spacing={ 3 } direction='column'>
            <Grid item>
              <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: getTheme().spacing(2) } }>
                <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center' } }>
                  <BsFillInfoCircleFill
                    style={ { marginRight: getTheme().spacing(2) } }
                    size={ 24 }
                  />
                  <Typography style={ { marginRight: getTheme().spacing(2) } } variant='h6'>
                    Information
                  </Typography>
                </div>
              </div>
              <Card>
                <Grid container spacing={ 3 }>
                  <CardContent>
                    {
                      userAccountDetails.map(({ field, label, disabled }) => {
                        return (
                          <BasicInformationField
                            key={ field }
                            disabled={ disabled }
                            title={ label }
                            field={ user[field] }
                          />
                        );
                      })
                    }
                  </CardContent>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}

export default AccountPage;
